/**
 * Extract subreddit name from URL
 * Supports:
 * 1. Subdomain: cfb.flairwizard.com → "cfb"
 * 2. Mirror host: flair.redditcfb.com → "cfb" (same app as cfb.flairwizard.com, no redirect)
 * 3. Path: /ncaaw (root deploy)
 * 4. Legacy path: /subs/redditflair/ncaaw → "ncaaw"
 */

/** Same tenant as cfb.flairwizard.com — DNS + SSL point here; no HTTP redirect. */
const REDDITCFB_MIRROR_HOST_TO_SUB: Record<string, string> = {
  'flair.redditcfb.com': 'cfb',
};

function mirrorSubredditFromHost(hostname: string): string | null {
  return REDDITCFB_MIRROR_HOST_TO_SUB[hostname.toLowerCase()] ?? null;
}

export function getSubredditFromUrl(): string | null {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  const mirror = mirrorSubredditFromHost(hostname);
  if (mirror) return mirror;

  // Pattern: Subdomain (e.g., cfb.flairwizard.com) — skip www
  const subdomainMatch = hostname.match(/^([^.]+)\.flairwizard\.com$/);
  if (subdomainMatch && subdomainMatch[1].toLowerCase() !== 'www') {
    return subdomainMatch[1].toLowerCase();
  }

  const pathParts = pathname.split('/').filter((p) => p);
  const excludedPaths = ['auth', 'admin', 'site-admin', 'api', 'static', 'assets', 'subs', 'place'];

  // Legacy: /subs/redditflair/ncaaw/...
  if (pathParts[0]?.toLowerCase() === 'subs' && pathParts[1]?.toLowerCase() === 'redditflair' && pathParts[2]) {
    const seg = pathParts[2].toLowerCase();
    if (!excludedPaths.includes(seg)) return seg;
  }

  if (pathParts.length > 0 && !excludedPaths.includes(pathParts[0].toLowerCase())) {
    return pathParts[0].toLowerCase();
  }

  return null;
}

/**
 * Tenant host: ncaaw.flairwizard.com (not www or apex), or a redditcfb mirror host.
 */
export function isSubdomainPattern(): boolean {
  const hostname = window.location.hostname;
  if (mirrorSubredditFromHost(hostname)) return true;
  return /^[^.]+\.flairwizard\.com$/i.test(hostname) && !/^www\.flairwizard\.com$/i.test(hostname);
}

/** www or apex — path-based /:subreddit; links should use https://{sub}.flairwizard.com/... */
export function isFlairwizardMarketingHost(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'www.flairwizard.com' || h === 'flairwizard.com';
}

export function subredditTenantOrigin(subreddit: string): string {
  return `https://${subreddit.toLowerCase()}.flairwizard.com`;
}

/**
 * Get the base URL for the current environment
 */
export function getBaseUrl(): string {
  return '/';
}

/**
 * Admin URL: tenant subdomain /admin; www/apex → https://{sub}.flairwizard.com/admin; else /admin/:subreddit.
 * May return an absolute https URL — use navigateOrExternal().
 */
export function adminPath(subreddit: string): string {
  if (isSubdomainPattern()) return '/admin';
  if (isFlairwizardMarketingHost()) return `${subredditTenantOrigin(subreddit)}/admin`;
  return `/admin/${subreddit}`;
}

/**
 * Trends URL — same rules as adminPath.
 */
export function trendsPath(subreddit: string): string {
  if (isSubdomainPattern()) return '/trends';
  if (isFlairwizardMarketingHost()) return `${subredditTenantOrigin(subreddit)}/trends`;
  return `/${subreddit}/trends`;
}

/** In-app navigate, or full navigation for cross-subdomain https URLs. */
export function navigateOrExternal(navigate: (to: string) => void, path: string): void {
  if (path.startsWith('https://') || path.startsWith('http://')) {
    window.location.assign(path);
  } else {
    navigate(path);
  }
}

/** Subreddit home: tenant subdomain root on FlairWizard marketing, else path /:sub */
export function subredditLandingUrl(subreddit: string): string {
  if (isFlairwizardMarketingHost()) return `${subredditTenantOrigin(subreddit)}/`;
  if (isSubdomainPattern()) return '/';
  return `/${subreddit}`;
}

/**
 * Main marketing landing (www). On tenant subdomains (e.g. wrestling.flairwizard.com), "/" is still
 * this app on the same host — use a full URL to reach www. Else relative "/" (path-based or www).
 */
export function marketingHomeUrl(): string {
  if (typeof window === 'undefined') return '/';
  if (!isSubdomainPattern()) return '/';
  return `${window.location.protocol}//www.flairwizard.com/`;
}

/**
 * Temporary: on https://cfb.flairwizard.com/ only, public flair tiles use 60×60; `*_place` CDN keys are
 * 60×40 Place tiles and look wrong at that size. Strip a trailing `_place` from `image_source` so the
 * standard asset (e.g. `team.png`) is requested instead of `team_place.png`.
 */
export function displayImageSourceForCfbFlairWizard(imageSource: string): string {
  if (typeof window === 'undefined') return imageSource;
  if (window.location.hostname !== 'cfb.flairwizard.com') return imageSource;
  const s = String(imageSource || '').trim();
  if (s.endsWith('_place')) return s.slice(0, -6);
  return s;
}

/** Minimal shape for flair grid text search (public picker + mod grant tab). */
export type FlairTemplateSearchFields = {
  flair_name?: string | null;
  abbreviated?: string | null;
  image_source?: string | null;
  /** Per-image label on wiki (master_sheet.image_name); may differ from flair_name when alternates exist */
  image_name?: string | null;
  text?: string | null;
};

/**
 * Substring search on display fields and image stem — not flair_code (use nested filter for structure).
 */
export function matchesFlairTemplateSearch(t: FlairTemplateSearchFields, queryRaw: string): boolean {
  const q = queryRaw.trim().toLowerCase();
  if (!q) return true;
  const fields = [t.flair_name, t.abbreviated, t.image_source, t.image_name, t.text].map((s) =>
    String(s ?? '')
      .toLowerCase()
      .trim()
  );
  return fields.some((f) => f.includes(q));
}
